import React, { useState, useEffect, useRef, useMemo, useReducer } from "react";
import useSWR from "swr";
import Fuse from "fuse.js";
import ContentCard from "./ContentCard";
import FullSiteSearch from "./FullSiteSearch";
import useKeyPress from "./useKeyPress";

const MAX_RESULTS_PER_GROUP = 5;
const ENDPOINT = "/wp-json/spring/v1/searchindex";
const CONTENT_TYPE_NAMES = {
  page: "Pages",
  program: "Programs",
  posts: "Stories",
  events: "Events",
  "collective-page": "Pages",
  "impact-capital-page": "Pages",
  "portfolio": "Portfolio",
};
const FUSE_OPTIONS = {
  shouldSort: true,
  threshold: 0.2,
  location: 0,
  distance: 1000,
  maxPatternLength: 32,
  minMatchCharLength: 1,
  keys: ["post_content", "post_title"],
};

async function fetcher(endpoint) {
  const response = await fetch(endpoint);
  const json = await response.json();
  return json;
}

// Keyboard navigation
const reducer = (selected, action) => {
  if (action.items?.length === 0 || !action.items) return null;

  switch (action.type) {
    case "arrowUp":
      if (selected === null) return null;
      return selected !== 1 ? selected - 1 : action.items.length;
    case "arrowDown":
      if (selected === null) return 1;
      return selected !== action.items.length ? selected + 1 : 1;
    case "Enter":
      return 1;
    case "select":
      return action.selection;
    default:
      return null;
  }
};

export default function Results({ searchTerm }) {
  const [results, setResults] = useState([]);
  const [sites, setSites] = useState([]);
  const [resultsOrdered, setResultsOrdered] = useState([]);
  const selectedListRef = useRef(null);
  const { data, isLoading, error } = useSWR(ENDPOINT, fetcher);

  const [selected, dispatch] = useReducer(reducer, null);

  const arrowUpPressed = useKeyPress("ArrowUp");
  const arrowDownPressed = useKeyPress("ArrowDown");
  const tabPressed = useKeyPress("Tab");

  const fuse = useMemo(() => {
    return data ? new Fuse(data, FUSE_OPTIONS) : null;
  }, [data]);

  const groupResults = (results) => {
    // split results into sites
    if (results.length < 1) return [];
    // create a sites object that splits the results array into 3 array based on each results "site" property
    const sites = results
      .map(({ item }) => item)
      .reduce((acc, item) => {
        // if site doesn't exist, create it
        if (!acc[item.site]) {
          acc[item.site] = [];
        }
        // check if there is a post type group in acc[item.site] that matches item.post_type
        // if post type group exists, add item to that group
        const hasGroup = (element) => element.post_type === item.post_type;
        const postTypeGroupIndex = acc[item.site].findIndex(hasGroup);

        if (postTypeGroupIndex > -1) {
          // if post type groups already has 5 items, do nothing
          if (
            acc[item.site][postTypeGroupIndex]["results"].length >=
            MAX_RESULTS_PER_GROUP
          ) {
            return acc;
          }
          acc[item.site][postTypeGroupIndex]["results"].push(item);
        }
        // else, create a new post type group and add entire group with item in results array
        else {
          const postTypeGroup = {
            post_type: item.post_type,
            results: [item],
          };
          acc[item.site].push(postTypeGroup);
        }
        return acc;
      }, {});

    const resultsOrdered = Object.values(sites)
      .flat(1)
      .map((group) => group.results)
      .flat(1);

    setResultsOrdered(resultsOrdered);

    return sites;
  };

  useEffect(() => {
    if (selectedListRef.current) {
      selectedListRef.current.focus();
    }
  }, [selected]);

  useEffect(() => {
    if (arrowUpPressed) {
      dispatch({ type: "arrowUp", items: resultsOrdered });
    }
  }, [arrowUpPressed]);

  useEffect(() => {
    if (arrowDownPressed || tabPressed) {
      dispatch({ type: "arrowDown", items: resultsOrdered });
    }
  }, [arrowDownPressed, tabPressed]);

  // Update results when search term changes
  useEffect(() => {
    setResults(fuse ? fuse.search(searchTerm) : []);
    dispatch({ items: resultsOrdered }); // remove for now, if focus is lost it will be null anyway?
  }, [searchTerm]);

  // every time the results array gets updated, order it
  useEffect(() => {
    const sites = groupResults(results);
    setSites(sites);
  }, [results]);

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (error) return (
    <>
  <div>Something went wrong. Try again later. </div>
  <FullSiteSearch searchTerm={searchTerm} />
    </>
  );

  let resultIndex = 0;
  return (
    <div className="search-results">
      {results.length > 0 &&
        Object.entries(sites).map(([site, groups]) => {
          return (
            <>
              <h2 className="search-results-site">{site.replace("-", " ")}</h2>
              <ul className="search-results-groups">
                {groups.map((group) => {
                  return (
                    <li
                      className="search-results-group"
                      key={site + "-" + group.post_type}
                    >
                      <div className={`${group.post_type}-entries`}>
                        <h3 className="search-result-group-name">
                          {CONTENT_TYPE_NAMES[group.post_type]}
                        </h3>
                        <ul className="group-list">
                          {group.results.map((result) => {
                            resultIndex++;
                            return (
                              <li
                                key={result.ID}
                                className={
                                  resultIndex === selected
                                    ? "group-list-item group-list-item--selected"
                                    : "group-list-item"
                                }
                              >
                                <a
                                  data-result-index={resultIndex}
                                  ref={
                                    resultIndex === selected
                                      ? selectedListRef
                                      : null
                                  }
                                  onFocus={(event) => {
                                    dispatch({
                                      type: "select",
                                      items: resultsOrdered,
                                      selection: parseInt(
                                        event.target.dataset.resultIndex
                                      ),
                                    });
                                  }}
                                  tabIndex={0}
                                  href={
                                    result.event_metadata?.link
                                      ? result.event_metadata.link
                                      : (
                                        result.portfolio_metadata?.website_url
                                        ? result.portfolio_metadata.website_url
                                        : result.guid
                                      )
                                  }
                                  className="content-card"
                                >
                                  <ContentCard post={result} />
                                </a>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </>
          );
        })}
      {searchTerm.length > 0 && <FullSiteSearch searchTerm={searchTerm} />}
    </div>
  );
}
